import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Card,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Typography,
  CircularProgress,
  Autocomplete,
  TextField,
  Box,
  Stack,
  IconButton,
  FormControlLabel,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  alpha,
} from "@mui/material";
import { motion } from "framer-motion";
import { Check, Edit } from "@mui/icons-material";
import SearchBar from "../../components/mui/SearchBar";
import { useDispatch } from "react-redux";
import FilterListOutlinedIcon from "@mui/icons-material/FilterListOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import {
  getAllBrands,
  parseBrandsPageResponse,
  updateBrand,
  updateBrandDiscount,
  uploadDocument,
  exportAllBrands,
  uploadBrandDiscount,
} from "../../actions/brandsAuth";
import { enqueueSnackbar } from "notistack";
import IOSSwitch from "../../style/globalStyles";
import { useNavigate } from "react-router-dom";
import CustomButton from "../../components/mui/CustomButton";
import ConfirmationModal from "../../components/ConfirmationModal";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import dayjs from "dayjs";
const columns = [
  "Brand",
  "Image",
  "Provider",
  "Discount",
  "Denomination",
  "Product",
  "Type",
  "Stock",
  "Category",
  "Active",
  "Premium",
  "Last Updated By",
  "Last Update",
];

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.04, duration: 0.45, ease: [0.22, 1, 0.36, 1] },
  }),
};

const PanelCard = ({ children, sx = {} }) => (
  <Card
    elevation={0}
    sx={{
      borderRadius: "24px",
      border: "1px solid",
      borderColor: alpha("#11409B", 0.08),
      bgcolor: "#fff",
      boxShadow: "0 4px 32px rgba(15, 23, 42, 0.06)",
      overflow: "hidden",
      ...sx,
    }}
  >
    {children}
  </Card>
);

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    "& fieldset": { borderColor: alpha("#11409B", 0.2) },
  },
};

const ROWS_PER_PAGE_OPTIONS = [10, 25, 30, 50, 100];

const BRAND_CATEGORIES = [
  "Food",
  "Quick Commerce",
  "Jewellary",
  "Grocery",
  "E-Commerce",
  "E-commerce",
  "Travel",
  "LifeStyle",
  "Entertainment",
  "Pharmacy",
  "Electronics",
];

const categoryAutocompleteComponentsProps = {
  popper: {
    placement: "bottom-start",
    style: { zIndex: 1500 },
    modifiers: [{ name: "preventOverflow", enabled: true }],
  },
  paper: {
    style: { maxHeight: 320 },
  },
};

const getLastUpdatedBy = (row) => {
  const by =
    row?.lastUpdateBy || "-"
  if (by === null || by === undefined || by === "") return "—";
  if (typeof by === "string") return by;
  return (
    by?.name ??
    by?.username ??
    by?.userName ??
    by?.email ??
    by?.phone ??
    "—"
  );
};

const formatLastUpdate = (row) => {
  const date =
    row?.updatedAt ??
    row?.UpdatedAt ??
    row?.lastUpdatedAt ??
    row?.LastUpdatedAt ??
    row?.lastUpdate;
  if (!date) return "—";
  const d = dayjs(date);
  return d.isValid() ? d.format("DD MMM YYYY, hh:mm A") : "—";
};

export const getBrandProvider = (row) => {
  const candidates = [
    row?.provider,
    row?.Provider,
    row?.apiProvider,
    row?.ApiProvider,
    row?.providerName,
    row?.ProviderName,
    row?.brandProvider,
    row?.BrandProvider,
    row?.source,
    row?.Source,
    row?.vendor,
    row?.Vendor,
  ];

  for (const c of candidates) {
    if (typeof c === "string" && c.trim()) {
      const val = c.trim();
      if (/gyftr/i.test(val)) return "GYFTR";
      if (/woohoo/i.test(val)) return "WOOHOO";
      return val;
    }
  }

  const bt = row?.brandType ?? row?.BrandType ?? row?.Brandtype;
  if (typeof bt === "string") {
    if (/gyftr/i.test(bt)) return "GYFTR";
    if (/woohoo/i.test(bt)) return "WOOHOO";
  }

  const pc = row?.brandProductCode ?? row?.BrandProductCode ?? row?.productCode;
  if (typeof pc === "string") {
    if (/gyftr/i.test(pc)) return "GYFTR";
    if (/woohoo/i.test(pc)) return "WOOHOO";
  }

  return "";
};

export const getBrandProviderDisplay = (row) => {
  const p = getBrandProvider(row);
  if (p === "GYFTR" || /gyftr/i.test(p)) return "Gyftr";
  if (p === "WOOHOO" || /woohoo/i.test(p)) return "Woohoo";
  if (p) return p;
  return "Woohoo";
};

export const matchesProvider = (row, targetProvider) => {
  if (!targetProvider || targetProvider === "ALL" || targetProvider === "all") {
    return true;
  }
  const target = String(targetProvider).toUpperCase();
  const bt = String(
    row?.brandType ?? row?.BrandType ?? row?.Brandtype ?? ""
  )
    .trim()
    .toUpperCase();

  if (target === "WOOHOO") {
    if (bt === "WOOHOO" || bt.includes("WOOHOO")) return true;
  }
  if (target === "GYFTR") {
    if (bt === "GYFTR" || bt.includes("GYFTR")) return true;
  }

  const display = getBrandProviderDisplay(row).toUpperCase();
  return display === target;
};

export const BrandManagement = ({ provider = "ALL", brandType }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const [currentPage, setCurrentPage] = useState(1);
  const [countPerPage, setCountPerPage] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [tempDiscountIndex, setTempDiscountIndex] = useState("");
  const [tempDiscount, setTempDiscount] = useState("");
  const [getAllData, setGetAllData] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [uploadingDiscount, setUploadingDiscount] = useState(false);
  const [discountUploadOpen, setDiscountUploadOpen] = useState(false);
  const [pendingDiscountFile, setPendingDiscountFile] = useState(null);
  const discountFileInputRef = useRef(null);

  // For Woohoo, use "brandType": "WOOHOO"
  // For Gyftr, use "provider": "Gyftr"
  const isWoohooTab =
    provider?.toUpperCase() === "WOOHOO" || brandType === "WOOHOO";
  const isGyftrTab =
    provider?.toUpperCase() === "GYFTR" || provider === "Gyftr";

  const activeBrandType = isWoohooTab ? "WOOHOO" : brandType || undefined;
  const activeProvider = isGyftrTab
    ? "Gyftr"
    : !isWoohooTab && provider && provider !== "ALL"
    ? provider
    : undefined;

  const targetFilter = isWoohooTab
    ? "WOOHOO"
    : isGyftrTab
    ? "GYFTR"
    : provider && provider !== "ALL"
    ? provider
    : undefined;

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);

  const pageBrands = getAllData;

  const stats = useMemo(() => {
    const list = pageBrands;
    return {
      total: totalElements,
      active: list.filter((b) => b.isActive).length,
      premium: list.filter((b) => b.isPremium).length,
      inStock: list.filter((b) => b.stockAvailable).length,
    };
  }, [pageBrands, totalElements]);
  const [tempImages, setTempImages] = useState([]);
  const Navigate = useNavigate();

  useEffect(() => {
    if (!localStorage.getItem("token")) {
      Navigate("/");
    }
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [provider, brandType]);

  const loadBrands = () => {
    setLoading(true);
    dispatch(
      getAllBrands(
        {
          page: Math.max(currentPage - 1, 0),
          size: Number(countPerPage),
          search: debouncedSearch.trim() || undefined,
          brandType: activeBrandType,
          provider: activeProvider,
        },
        (data) => {
          const parsed = data?.parsed ?? parseBrandsPageResponse(data);
          const rawContent = parsed.content || [];

          if (targetFilter) {
            const hasMismatched = rawContent.some(
              (b) => !matchesProvider(b, targetFilter)
            );
            if (hasMismatched) {
              const filtered = rawContent.filter((b) =>
                matchesProvider(b, targetFilter)
              );
              setGetAllData(filtered);
              setTotalElements(filtered.length);
              setTotalPages(Math.max(Math.ceil(filtered.length / countPerPage), 1));
              setLoading(false);
              return;
            }
          }

          setGetAllData(rawContent);
          setTotalElements(parsed.totalElements);
          setTotalPages(parsed.totalPages);
          setLoading(false);
        },
        (err) => {
          console.error(err);
          setLoading(false);
        }
      )
    );
  };

  useEffect(() => {
    loadBrands();
  }, [dispatch, currentPage, countPerPage, debouncedSearch, provider, brandType]);

  const fetchAllBrandsForExport = (onComplete) => {
    const pageSize = 100;
    const search = debouncedSearch.trim() || undefined;

    const fetchPage = (page, accumulated = []) => {
      dispatch(
        getAllBrands(
          {
            page,
            size: pageSize,
            search,
            brandType: activeBrandType,
            provider: activeProvider,
          },
          (data) => {
            const parsed = data?.parsed ?? parseBrandsPageResponse(data);
            const merged = [...accumulated, ...parsed.content];
            const hasMore =
              merged.length < parsed.totalElements &&
              parsed.content.length === pageSize;

            if (hasMore) {
              fetchPage(page + 1, merged);
              return;
            }

            const finalBrands = targetFilter
              ? merged.filter((b) => matchesProvider(b, targetFilter))
              : merged;
            onComplete(finalBrands);
          },
          (err) => {
            console.error(err);
            const finalBrands = targetFilter
              ? accumulated.filter((b) => matchesProvider(b, targetFilter))
              : accumulated;
            onComplete(finalBrands);
          }
        )
      );
    };

    fetchPage(0);
  };

  const exportToExcel = () => {
    setExporting(true);
    fetchAllBrandsForExport((brandsToExport) => {
      const header = [
      "Brand Name",
      "Provider",
      "Siripay Discount",
      "Denomination List",
      "Brand Product Code",
      "Brand Type",
      "Stock Availability",
      "Date Added",
      "Category Name",
      "Active Status",
      "Premium Status",
      "Last Updated By",
      "Last Update",
    ];

    const truncateString = (str, maxLength = 32767) => {
      if (typeof str !== "string") {
        return str;
      }
      return str.length > maxLength
        ? str.substring(0, maxLength - 3) + "..."
        : str;
    };

    const dataToExport = brandsToExport.map((brand) => [
      truncateString(brand.BrandName || brand.brandName || ""),
      truncateString(getBrandProviderDisplay(brand)),
      brand.discount || "",
      truncateString(
        brand.denominationList ? brand.denominationList.join(", ") : "--"
      ),
      truncateString(brand.BrandProductCode || brand.brandProductCode || ""),
      truncateString(brand.BrandType || brand.brandType || ""),
      brand.stockAvailable ? "Available" : "Out of stock",
      brand.createdAt ? new Date(brand.createdAt).toLocaleDateString() : "--",
      truncateString(brand.Category ? brand.Category.join(", ") : "--"),
      brand.isActive ? "Active" : "Inactive",
      brand.isPremium ? "Active" : "Inactive",
      getLastUpdatedBy(brand),
      formatLastUpdate(brand),
    ]);

    dataToExport.unshift(header);

    const worksheet = XLSX.utils.aoa_to_sheet(dataToExport);

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Brands Data");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const blob = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });

    saveAs(blob, "brands_data.xlsx");
    setExporting(false);
    });
  };

  const downloadAllBrands = () => {
    setDownloadingAll(true);
    dispatch(
      exportAllBrands(
        (response) => {
          const blob = new Blob([response.data], {
            type:
              response.headers["content-type"] ||
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const disposition = response.headers["content-disposition"];
          let filename = "brands_export.xlsx";
          if (disposition) {
            const match = disposition.match(
              /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
            );
            if (match?.[1]) {
              filename = match[1].replace(/['"]/g, "");
            }
          }
          saveAs(blob, filename);
          enqueueSnackbar("All brands downloaded successfully", {
            variant: "success",
          });
          setDownloadingAll(false);
        },
        (err) => {
          enqueueSnackbar(
            err?.response?.data?.message || "Failed to download brands",
            { variant: "error" }
          );
          setDownloadingAll(false);
        }
      )
    );
  };

  const handleDiscountFileChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    if (!/\.(xls|xlsx)$/i.test(file.name)) {
      enqueueSnackbar("Please upload a .xls or .xlsx file", {
        variant: "warning",
      });
      return;
    }

    setPendingDiscountFile(file);
    setDiscountUploadOpen(true);
  };

  const handleDiscountUploadClose = () => {
    if (uploadingDiscount) return;
    setDiscountUploadOpen(false);
    setPendingDiscountFile(null);
  };

  const handleDiscountUploadConfirm = () => {
    if (!pendingDiscountFile) return;

    setUploadingDiscount(true);
    dispatch(
      uploadBrandDiscount(
        pendingDiscountFile,
        () => {
          enqueueSnackbar("Discount file uploaded successfully", {
            variant: "success",
          });
          loadBrands();
          setUploadingDiscount(false);
          setDiscountUploadOpen(false);
          setPendingDiscountFile(null);
        },
        (err) => {
          enqueueSnackbar(
            err?.response?.data?.message || "Failed to upload discount file",
            { variant: "error" }
          );
          setUploadingDiscount(false);
          setDiscountUploadOpen(false);
          setPendingDiscountFile(null);
        }
      )
    );
  };

  const [isEditing, setIsEditing] = useState(null);
  const [tempImage, setTempImage] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);

  const handleEditClick = (row, index) => {
    if (isEditing === index) {
      if (selectedImage) {
        uploadImage(selectedImage, index, row);
      }
      setIsEditing(null);
    } else {
      setTempImage(getBrandField(row, "brandImage", "BrandImage"));
      setIsEditing(index);
      document.getElementById(`file-input-${index}`).click();
    }
  };

  const handleFileChange = (e, index) => {
    if (e.target.files && e.target.files[0]) {
      const fileName = e.target.files[0].name.toLowerCase();
      if (
        fileName.includes("jpg") ||
        fileName.includes("jpeg") ||
        fileName.includes("png")
      ) {
        setSelectedImage(e.target.files[0]);
        setTempImage(URL.createObjectURL(e.target.files[0]));
        setIsEditing(index);
      } else {
        enqueueSnackbar("Please upload a valid image", { variant: "warning" });
      }
    }
  };

  const uploadImage = (file, index, row) => {
    let formData = new FormData();
    formData.append("file", file);
    dispatch(
      uploadDocument(
        formData,
        (data) => {
          const newImageUrl = data?.data?.data?.publicUrl;
          updateBrandStatus(row, { brandImage: newImageUrl });
        },
        (err) => {
          console.log(err);
        }
      )
    );
  };

  const updateBrandStatus = (row, overrides = {}) => {
    const brandId = getBrandField(row, "id", "_id");
    const payload = {
      brandName: getBrandField(row, "brandName", "BrandName"),
      brandType: getBrandField(row, "brandType", "Brandtype", "BrandType"),
      onlineRedemptionUrl: getBrandField(row, "onlineRedemptionUrl"),
      brandImage: getBrandField(row, "brandImage", "BrandImage"),
      stockAvailable: Boolean(row?.stockAvailable),
      minDenomination: getBrandField(row, "minDenomination"),
      maxDenomination: getBrandField(row, "maxDenomination"),
      category: row?.category || row?.Category || [],
      descriptions: getBrandField(row, "descriptions"),
      tnc: getBrandField(row, "tnc"),
      importantInstruction: getBrandField(row, "importantInstruction"),
      discount: Number(getBrandField(row, "discount")) || 0,
      isActive: Boolean(row?.isActive),
      isPremium: Boolean(row?.isPremium),
      provider: getBrandField(row, "provider", "Provider", "apiProvider"),
      ...overrides,
    };
    dispatch(
      updateBrand(
        brandId,
        payload,
        (data) => {
          enqueueSnackbar("Brand Updated Successfully", { variant: "success" });
          loadBrands();
        },
        (error) => {
          const errorMessage =
            typeof error?.response?.data?.message === "string" &&
            error?.response?.data?.message?.trim()
              ? error.response.data.message
              : "Failed to update brand";
          enqueueSnackbar(errorMessage, { variant: "error" });
          console.log(error);
        }
      )
    );
  };

  const getBrandField = (row, ...keys) => {
    for (const key of keys) {
      if (row?.[key] !== undefined && row?.[key] !== null && row?.[key] !== "") {
        return row[key];
      }
    }
    return "--";
  };

  const updateDiscountById = (row, discountValue) => {
    const brandId = getBrandField(row, "id", "_id");
    dispatch(
      updateBrandDiscount(
        brandId,
        { discount: Number(discountValue) || 0 },
        () => {
          enqueueSnackbar("Discount Updated Successfully", { variant: "success" });
          setTempDiscountIndex("");
          loadBrands();
        },
        (error) => {
          const errorMessage =
            typeof error?.response?.data?.message === "string" &&
            error?.response?.data?.message?.trim()
              ? error.response.data.message
              : "Failed to update discount";
          enqueueSnackbar(errorMessage, {
            variant: "error",
          });
        }
      )
    );
  };

  const pageStart =
    totalElements === 0 ? 0 : (currentPage - 1) * countPerPage + 1;
  const pageEnd = Math.min(currentPage * countPerPage, totalElements);

  const StatMini = ({ label, value, color }) => (
    <Box
      sx={{
        p: 2,
        borderRadius: "16px",
        bgcolor: alpha(color, 0.08),
        border: `1px solid ${alpha(color, 0.15)}`,
        textAlign: "center",
      }}
    >
      <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography sx={{ fontWeight: 800, fontSize: "1.5rem", color: "#0f172a" }}>
        {value}
      </Typography>
    </Box>
  );

  return (
    <>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: "Total brands", value: stats.total, color: "#4f46e5" },
          { label: "Active", value: stats.active, color: "#16a34a" },
          { label: "Premium", value: stats.premium, color: "#d97706" },
          { label: "In stock", value: stats.inStock, color: "#0891b2" },
        ].map((s, i) => (
          <Grid item xs={6} md={3} key={s.label}>
            <motion.div custom={i} initial="hidden" animate="visible" variants={fadeUp}>
              <StatMini {...s} />
            </motion.div>
          </Grid>
        ))}
      </Grid>

      <motion.div custom={4} initial="hidden" animate="visible" variants={fadeUp}>
        <PanelCard sx={{ mb: 3, p: { xs: 2, md: 2.5 } }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2} mb={2}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <FilterListOutlinedIcon sx={{ color: "#11409B" }} />
              <Typography sx={{ fontWeight: 700, color: "#0f172a" }}>Search & export</Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
              <input
                ref={discountFileInputRef}
                type="file"
                accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                style={{ display: "none" }}
                onChange={handleDiscountFileChange}
              />
              <CustomButton
                variant="contained"
                onClick={downloadAllBrands}
                disabled={downloadingAll}
                startIcon={<FileDownloadOutlinedIcon />}
                sx={{
                  height: "40px",
                  borderRadius: "12px",
                  textTransform: "none",
                  fontWeight: 600,
                  background: "linear-gradient(135deg, #11409B 0%, #2563eb 100%)",
                }}
              >
                {downloadingAll
                  ? "Downloading…"
                  : isWoohooTab
                  ? "Download Woohoo brands"
                  : isGyftrTab
                  ? "Download Gyftr brands"
                  : "Download all brands"}
              </CustomButton>
              <CustomButton
                variant="outlined"
                onClick={() => discountFileInputRef.current?.click()}
                disabled={uploadingDiscount}
                startIcon={<FileUploadOutlinedIcon />}
                sx={{
                  height: "40px",
                  borderRadius: "12px",
                  textTransform: "none",
                  fontWeight: 600,
                  borderColor: alpha("#11409B", 0.35),
                  color: "#11409B",
                }}
              >
                {uploadingDiscount ? "Uploading…" : "Upload discount"}
              </CustomButton>
              <CustomButton
                variant="outlined"
                onClick={exportToExcel}
                disabled={exporting}
                startIcon={<FileDownloadOutlinedIcon />}
                sx={{
                  height: "40px",
                  borderRadius: "12px",
                  textTransform: "none",
                  fontWeight: 600,
                  borderColor: alpha("#11409B", 0.35),
                  color: "#11409B",
                }}
              >
                {exporting ? "Exporting…" : "Export filtered"}
              </CustomButton>
            </Stack>
          </Stack>
          <SearchBar
            onSearch={handleSearch}
            searchTerm={searchTerm}
            onClear={() => handleSearch("")}
            sWidth="100%"
            height="2.75rem"
            placeholder={
              isWoohooTab
                ? "Search Woohoo brands by name, SKU, or category"
                : isGyftrTab
                ? "Search Gyftr brands by name, SKU, or category"
                : "Search by name, SKU, or category"
            }
          />
          {searchTerm.trim() ? (
            <Typography variant="body2" sx={{ mt: 1.5, color: "#64748b" }}>
              {totalElements} brand{totalElements === 1 ? "" : "s"} match your search
            </Typography>
          ) : null}
        </PanelCard>
      </motion.div>

      <motion.div
        custom={5}
        initial="hidden"
        animate="visible"
        variants={fadeUp}
        style={{ overflow: "visible" }}
      >
        {loading ? (
          <PanelCard>
            <Stack alignItems="center" py={10}>
              <CircularProgress sx={{ color: "#11409B" }} />
              <Typography sx={{ mt: 2, color: "#64748b" }}>Loading brands…</Typography>
            </Stack>
          </PanelCard>
        ) : pageBrands.length === 0 ? (
          <PanelCard>
            <Typography textAlign="center" py={6} sx={{ color: "#64748b" }}>
              No brands found
            </Typography>
          </PanelCard>
        ) : (
          <PanelCard sx={{ overflow: "visible" }}>
            <TableContainer
              sx={{
                display: "block",
                width: "100%",
                maxWidth: "100%",
                overflowX: "auto",
                overflowY: "visible",
                WebkitOverflowScrolling: "touch",
              }}
            >
            <Table sx={{ minWidth: 1800, width: "max-content" }}>
              <TableHead>
                <TableRow sx={{ bgcolor: alpha("#11409B", 0.04) }}>
                  {columns.map((column) => (
                    <TableCell
                      key={column}
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.7rem",
                        color: "#11409B",
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        py: 2,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {column}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {pageBrands.map((row, index) => {
                  const rowKey = getBrandField(row, "id", "_id");
                  const imgSrc =
                    isEditing === index
                      ? tempImage
                      : getBrandField(row, "brandImage", "BrandImage");
                  return (
                    <TableRow
                      key={rowKey !== "--" ? rowKey : index}
                      sx={{
                        "&:hover": { bgcolor: alpha("#11409B", 0.03) },
                        "& td": { borderColor: alpha("#11409B", 0.06), py: 1.5, verticalAlign: "top" },
                      }}
                    >
                      <TableCell sx={{ minWidth: 200 }}>
                        <Typography sx={{ fontWeight: 700, color: "#0f172a" }}>
                          {getBrandField(row, "brandName", "BrandName")}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#94a3b8", fontFamily: "monospace" }}>
                          {rowKey}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            position: "relative",
                            borderRadius: "12px",
                            overflow: "hidden",
                            border: `1px solid ${alpha("#11409B", 0.12)}`,
                            width: 112,
                          }}
                        >
                          <Box
                            component="img"
                            src={imgSrc !== "--" ? imgSrc : undefined}
                            alt=""
                            sx={{ width: 112, height: 64, objectFit: "cover", display: "block", bgcolor: "#f1f5f9" }}
                          />
                          <input
                            type="file"
                            id={`file-input-${index}`}
                            hidden
                            accept="image/*"
                            onChange={(e) => handleFileChange(e, index)}
                          />
                          <IconButton
                            size="small"
                            onClick={() => handleEditClick(row, index)}
                            sx={{
                              position: "absolute",
                              bottom: 4,
                              right: 4,
                              bgcolor: alpha("#fff", 0.9),
                              "&:hover": { bgcolor: "#fff" },
                            }}
                          >
                            {isEditing === index ? (
                              <Check fontSize="small" color="success" />
                            ) : (
                              <Edit fontSize="small" color="primary" />
                            )}
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ minWidth: 120 }}>
                        {(() => {
                          const prov = getBrandProviderDisplay(row);
                          const isGyftr = prov.toLowerCase() === "gyftr";
                          return (
                            <Chip
                              label={prov}
                              size="small"
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.75rem",
                                color: isGyftr ? "#e11d48" : "#2563eb",
                                bgcolor: isGyftr
                                  ? alpha("#e11d48", 0.1)
                                  : alpha("#2563eb", 0.1),
                                border: `1px solid ${
                                  isGyftr
                                    ? alpha("#e11d48", 0.25)
                                    : alpha("#2563eb", 0.25)
                                }`,
                              }}
                            />
                          );
                        })()}
                      </TableCell>
                      <TableCell sx={{ minWidth: 120 }}>
                        <TextField
                          size="small"
                          placeholder="%"
                          value={tempDiscountIndex === index ? tempDiscount : getBrandField(row, "discount")}
                          onChange={(e) => setTempDiscount(e.target.value)}
                          disabled={tempDiscountIndex !== index}
                          sx={{ width: 90, ...fieldSx }}
                          InputProps={{
                            endAdornment:
                              tempDiscountIndex === index ? (
                                <Check sx={{ fontSize: 18, cursor: "pointer" }} onClick={() => updateDiscountById(row, tempDiscount)} />
                              ) : (
                                <Edit
                                  sx={{ fontSize: 18, cursor: "pointer" }}
                                  onClick={() => {
                                    setTempDiscount(getBrandField(row, "discount"));
                                    setTempDiscountIndex(index);
                                  }}
                                />
                              ),
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: "#475569" }}>
                          {getBrandField(row, "minDenomination")} – {getBrandField(row, "maxDenomination")}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={getBrandField(row, "brandProductCode", "BrandProductCode")} size="small" sx={{ fontWeight: 600, maxWidth: 140 }} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{getBrandField(row, "brandType", "Brandtype", "BrandType")}</Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Chip
                            label={row?.stockAvailable ? "In stock" : "Out of stock"}
                            size="small"
                            sx={{
                              fontWeight: 700,
                              color: row?.stockAvailable ? "#16a34a" : "#dc2626",
                              bgcolor: row?.stockAvailable
                                ? alpha("#16a34a", 0.12)
                                : alpha("#dc2626", 0.12),
                            }}
                          />
                          <FormControlLabel
                            control={
                              <IOSSwitch
                                checked={Boolean(row.stockAvailable)}
                                onChange={(e) =>
                                  updateBrandStatus(row, {
                                    stockAvailable: e.target.checked,
                                  })
                                }
                              />
                            }
                            label=""
                            sx={{ m: 0 }}
                          />
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ minWidth: 260 }}>
                        <Autocomplete
                          multiple
                          size="small"
                          disableCloseOnSelect
                          value={row?.category || row?.Category || []}
                          onChange={(_, newValue) => updateBrandStatus(row, { category: newValue })}
                          options={BRAND_CATEGORIES}
                          componentsProps={categoryAutocompleteComponentsProps}
                          sx={{ minWidth: 240, width: 240 }}
                          renderInput={(params) => (
                            <TextField {...params} label="Category" sx={fieldSx} />
                          )}
                        />
                      </TableCell>
                      <TableCell>
                        <FormControlLabel
                          control={
                            <IOSSwitch
                              checked={Boolean(row.isActive)}
                              onChange={(e) => updateBrandStatus(row, { isActive: e.target.checked })}
                            />
                          }
                          label=""
                        />
                      </TableCell>
                      <TableCell>
                        <FormControlLabel
                          control={
                            <IOSSwitch
                              checked={Boolean(row.isPremium)}
                              onChange={(e) => updateBrandStatus(row, { isPremium: e.target.checked })}
                            />
                          }
                          label=""
                        />
                      </TableCell>
                      <TableCell sx={{ minWidth: 140 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: "#0f172a" }}>
                          {getLastUpdatedBy(row)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 160 }}>
                        <Typography variant="body2" sx={{ color: "#64748b", whiteSpace: "nowrap" }}>
                          {formatLastUpdate(row)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            </TableContainer>
          </PanelCard>
        )}
      </motion.div>

      {!loading && totalElements > 0 && (
        <motion.div
          custom={6}
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          style={{ overflow: "visible" }}
        >
          <PanelCard sx={{ mt: 3, p: 2, overflow: "visible" }}>
            <Stack direction={{ xs: "column", sm: "row" }} alignItems="center" justifyContent="space-between" spacing={2}>
              <Typography sx={{ color: "#64748b", fontWeight: 500 }}>
                Showing {pageStart}–{pageEnd} of {totalElements}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={2}>
                <FormControl size="small" sx={{ minWidth: 100 }}>
                  <InputLabel id="brand-rows-per-page-label">Rows</InputLabel>
                  <Select
                    labelId="brand-rows-per-page-label"
                    value={countPerPage}
                    label="Rows"
                    onChange={(e) => {
                      setCountPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    MenuProps={{
                      disableScrollLock: true,
                      PaperProps: { sx: { maxHeight: 320 } },
                    }}
                    sx={{
                      height: "2.5rem",
                      borderRadius: "10px",
                      bgcolor: "#fff",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: alpha("#11409B", 0.2),
                      },
                    }}
                  >
                    {ROWS_PER_PAGE_OPTIONS.map((n) => (
                      <MenuItem key={n} value={n}>
                        {n}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <IconButton
                  onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                  disabled={currentPage === 1}
                  sx={{ border: "1px solid", borderColor: alpha("#11409B", 0.15), borderRadius: "12px" }}
                >
                  <KeyboardArrowLeftIcon />
                </IconButton>
                <Typography sx={{ fontWeight: 600 }}>{currentPage} / {totalPages}</Typography>
                <IconButton
                  onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                  disabled={currentPage >= totalPages}
                  sx={{ border: "1px solid", borderColor: alpha("#11409B", 0.15), borderRadius: "12px" }}
                >
                  <KeyboardArrowRightIcon />
                </IconButton>
              </Stack>
            </Stack>
          </PanelCard>
        </motion.div>
      )}
      <ConfirmationModal
        open={discountUploadOpen}
        handleClose={handleDiscountUploadClose}
        handleConfirm={handleDiscountUploadConfirm}
        title="Confirm discount upload"
        message={
          pendingDiscountFile
            ? `Upload "${pendingDiscountFile.name}" to update brand discounts? This will apply changes from the spreadsheet.`
            : "Upload this file to update brand discounts?"
        }
        confirmLabel="Upload"
        loading={uploadingDiscount}
      />
    </>
  );
};
